import React, { createContext, useContext, useState, useEffect, ReactNode, useRef, useCallback } from 'react';
import { Session, User as SupabaseUser } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '../lib/supabase';
import { withExponentialBackoff, isNetworkError, getAuthErrorMessage } from '../lib/auth-utils';
import { sessionActivity } from '../lib/session-activity';
import { authAudit } from '../lib/auth-audit';
import GTMService from '../lib/gtm';

interface CoreAuthContextType {
  session: Session | null;
  supabaseUser: SupabaseUser | null;
  isLoading: boolean;
  isInitialized: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  register: (email: string, password: string, metadata?: any) => Promise<any>;
  resetPassword: (email: string) => Promise<void>;
  resendEmailConfirmation: (email: string) => Promise<void>;
  refreshSession: () => Promise<Session | null>;
  // OTP methods
  signInWithOtp: (email: string, shouldCreateUser?: boolean) => Promise<void>;
  verifyOtp: (email: string, token: string, type: 'signup' | 'recovery' | 'email_change' | 'email') => Promise<void>;
  resetPasswordWithOtp: (email: string) => Promise<void>;
}

const CoreAuthContext = createContext<CoreAuthContextType | undefined>(undefined);

// Session management state (replacing global variables)
interface SessionCache {
  session: Session | null;
  timestamp: number;
}

export function CoreAuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<SupabaseUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  
  // Replace global variables with refs
  const sessionCacheRef = useRef<SessionCache>({ session: null, timestamp: 0 });
  const initializingRef = useRef(false);
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Session cache duration (30 seconds)
  const SESSION_CACHE_DURATION = 30000;

  const getSessionWithCache = useCallback(async (forceRefresh = false): Promise<Session | null> => {
    const now = Date.now();
    const cache = sessionCacheRef.current;
    
    // Use cached session if fresh
    if (!forceRefresh && cache.session && (now - cache.timestamp) < SESSION_CACHE_DURATION) {
      return cache.session;
    }
    
    try {
      
      const sessionResult = await withExponentialBackoff(
        async () => {
          const result = await supabase.auth.getSession();
          if (result.error) throw result.error;
          return result;
        },
        {
          maxRetries: 3,
          initialDelay: 1000,
          shouldRetry: (error) => isNetworkError(error)
        }
      );
      
      if (sessionResult.data.session) {
        sessionCacheRef.current = {
          session: sessionResult.data.session,
          timestamp: now
        };
        return sessionResult.data.session;
      }
      
      sessionCacheRef.current = { session: null, timestamp: 0 };
      return null;
    } catch (error) {
      console.error('Session retrieval error:', error);
      
      // Return cached session as fallback if available
      if (cache.session && (now - cache.timestamp) < 300000) { // 5 min tolerance
        return cache.session;
      }
      
      sessionCacheRef.current = { session: null, timestamp: 0 };
      return null;
    }
  }, []);

  const refreshSession = useCallback(async (): Promise<Session | null> => {
    return getSessionWithCache(true);
  }, [getSessionWithCache]);

  // Initialize authentication
  useEffect(() => {
    if (isInitialized || initializingRef.current) {
      return;
    }

    initializingRef.current = true;

    const initializeAuth = async () => {
      try {
        const currentSession = await getSessionWithCache();
        
        if (currentSession) {
          setSession(currentSession);
          setSupabaseUser(currentSession.user);
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
      } finally {
        setIsLoading(false);
        setIsInitialized(true);
        initializingRef.current = false;
      }
    };

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        // Prevent unnecessary re-renders during payment flow
        const currentSession = sessionCacheRef.current.session;
        const sessionChanged = newSession?.access_token !== currentSession?.access_token;
        
        
        // Only update state if session actually changed
        if (sessionChanged) {
          setSession(newSession);
          setSupabaseUser(newSession?.user || null);
        }
        
        if (newSession) {
          sessionCacheRef.current = {
            session: newSession,
            timestamp: Date.now()
          };
          
          // Start session activity tracking (only on actual session change)
          if (sessionChanged) {
            sessionActivity.start(newSession.access_token.substring(0, 8));
            
            // Set Sentry user context
            Sentry.setUser({
              id: newSession.user.id,
              email: newSession.user.email,
            });
          }
          
          // Log auth events
          if (event === 'SIGNED_IN') {
            await authAudit.logLogin(newSession.user.id, 'password', { event });
          } else if (event === 'TOKEN_REFRESHED') {
            await authAudit.logSessionEvent(newSession.user.id, 'session_refreshed');
          }
        } else {
          sessionCacheRef.current = { session: null, timestamp: 0 };
          
          // Stop session activity tracking
          sessionActivity.stop();
          
          // Clear Sentry user context
          Sentry.setUser(null);
          
          // Log sign out
          if (event === 'SIGNED_OUT' && supabaseUser) {
            await authAudit.logLogout(supabaseUser.id);
          }
        }
        
        if (!isInitialized) {
          setIsLoading(false);
          setIsInitialized(true);
        }
      }
    );

    // Handle visibility changes
    const handleVisibilityChange = () => {
      if (!document.hidden && session) {
        refreshSession();
      }
    };

    // **NEW: Periodic session validation (every 5 minutes)**
    const SESSION_VALIDATION_INTERVAL = 5 * 60 * 1000; // 5 minutes
    let validationInterval: NodeJS.Timeout | null = null;

    const validateAndRefreshSession = async () => {
      if (!session) return;
      
      try {
        
        // Check if current session is still valid
        const { data: { session: validSession }, error } = await supabase.auth.getSession();
        
        if (error || !validSession) {
          await supabase.auth.signOut();
          return;
        }
        
        // Check if session is about to expire (within 10 minutes)
        const expiresAt = validSession.expires_at ? new Date(validSession.expires_at * 1000) : null;
        if (expiresAt) {
          const now = new Date();
          const timeUntilExpiry = expiresAt.getTime() - now.getTime();
          
          if (timeUntilExpiry < 10 * 60 * 1000) { // Less than 10 minutes
            const { data: { session: refreshedSession }, error: refreshError } = 
              await supabase.auth.refreshSession();
            
            if (!refreshError && refreshedSession) {
            }
          }
        }
      } catch (error) {
        console.error('Session validation error:', error);
      }
    };

    // Start periodic validation when session exists
    if (session) {
      validationInterval = setInterval(validateAndRefreshSession, SESSION_VALIDATION_INTERVAL);
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize
    initializeAuth();

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
      }
      
      if (validationInterval) {
        clearInterval(validationInterval);
      }
    };
  }, [getSessionWithCache, refreshSession, session, isInitialized]);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    
    try {
      const { data, error } = await withExponentialBackoff(
        async () => {
          const result = await supabase.auth.signInWithPassword({ email, password });
          if (result.error) {
            // Log failed login attempt
            await authAudit.logFailedLogin(email, result.error.message);
            throw new Error(getAuthErrorMessage(result.error));
          }
          return result;
        },
        {
          maxRetries: 2,
          initialDelay: 1000,
          shouldRetry: (error) => isNetworkError(error)
        }
      );

      if (error) throw error;
      
      
      // Manually trigger session update since onAuthStateChange might not fire immediately
      if (data.session) {
        setSession(data.session);
        setSupabaseUser(data.session.user);
        
        sessionCacheRef.current = {
          session: data.session,
          timestamp: Date.now()
        };
        
        // Start session activity tracking
        sessionActivity.start(data.session.access_token.substring(0, 8));
        
        // Set Sentry user context
        Sentry.setUser({
          id: data.session.user.id,
          email: data.session.user.email,
        });
      }
      
      // Success logging handled by auth state change listener
    } catch (error: any) {
      console.error('Login error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'login',
          component: 'CoreAuthContext',
        },
        extra: {
          email: email,
          userAgent: navigator.userAgent,
        },
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Track logout before clearing session
      if (session?.user?.id) {
        GTMService.trackLogout(session.user.id);
      }
      
      // Clear cache immediately
      sessionCacheRef.current = { session: null, timestamp: 0 };
      setSession(null);
      setSupabaseUser(null);
      
      // Clear Sentry user context
      Sentry.setUser(null);
      
      const { error } = await supabase.auth.signOut();
      if (error) {
        // Handle the case where user was deleted (account deletion flow)
        // In this case, the user no longer exists in auth, so logout failing is expected
        const isUserNotFound = error.message?.includes('User from sub claim in JWT does not exist') || 
                              error.message?.includes('user_not_found') ||
                              error.status === 403 ||
                              (error as any)?.code === 'user_not_found';
        
        if (isUserNotFound) {
          // Clear any remaining session data
          sessionCacheRef.current = { session: null, timestamp: 0 };
          setSession(null);
          setSupabaseUser(null);
          return;
        }
        throw error;
      }
      
    } catch (error) {
      console.error('Logout error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'logout',
          component: 'CoreAuthContext',
        },
      });
      throw error;
    }
  };

  const loginWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/onboarding?from=google`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Google login error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'google-login',
          component: 'CoreAuthContext',
        },
      });
      throw error;
    }
  };

  const register = async (email: string, password: string, metadata?: any) => {
    const shutdownError = new Error('Registrations are closed. Rulevia is shutting down on July 14, 2026.');
    shutdownError.name = 'ServiceShutdownError';
    throw shutdownError;

    setIsLoading(true);

    try {
      // Dynamic redirect URL based on environment
      const isDev = import.meta.env.VITE_APP_ENV === 'development' || window.location.hostname === 'localhost';
      const redirectUrl = isDev
        ? 'http://localhost:5173/onboarding?step=subscription'
        : 'https://www.rulevia.com/onboarding?step=subscription';


      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: redirectUrl
        }
      });

      if (error) throw error;

      // Check for duplicate email patterns

      // CRITICAL: Check for duplicate email patterns
      let isDuplicateEmail = false;
      let duplicateReason = '';

      // Pattern 1: No user and no session
      if (!data.user && !data.session) {
        isDuplicateEmail = true;
        duplicateReason = 'No user and no session returned';
      }

      // Pattern 2: User already exists (email is already confirmed)
      if (data.user && data.user.email_confirmed_at) {
        isDuplicateEmail = true;
        duplicateReason = `User returned with confirmed email: ${data.user.email_confirmed_at}`;
      }

      // Pattern 3 REMOVED: Previous logic incorrectly flagged normal new registrations as duplicates
      // When email confirmation is required, Supabase creates a user but no session until email is confirmed
      // This is normal behavior, not a duplicate email scenario

      // Pattern 4: Multiple identities detected
      if (data.user && data.user.identities && data.user.identities.length > 1) {
        isDuplicateEmail = true;
        duplicateReason = 'Multiple identities detected';
      }

      // If duplicate detected, throw error
      if (isDuplicateEmail) {
        console.error('🚨 DUPLICATE EMAIL DETECTED:', duplicateReason);
        const duplicateError = new Error('An account with this email already exists');
        duplicateError.name = 'DuplicateEmailError';
        throw duplicateError;
      }

      // Return user data for marketing consent handling
      return data.user;

    } catch (error) {
      console.error('Registration error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'register',
          component: 'CoreAuthContext',
        },
        extra: {
          email: email,
          userAgent: navigator.userAgent,
        },
      });
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;
    } catch (error) {
      console.error('Password reset error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'reset-password',
          component: 'CoreAuthContext',
        },
        extra: {
          email: email,
        },
      });
      throw error;
    }
  };

  const resendEmailConfirmation = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
        options: {
          emailRedirectTo: `${window.location.origin}/onboarding?step=subscription`
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Resend email error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'resend-email',
          component: 'CoreAuthContext',
        },
        extra: {
          email: email,
        },
      });
      throw error;
    }
  };

  // OTP Methods
  const signInWithOtp = async (email: string, shouldCreateUser = true) => {
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser,
          emailRedirectTo: `${window.location.origin}/onboarding?step=subscription`
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Sign in with OTP error:', error);
      Sentry.captureException(error, {
        tags: {
          operation: 'signin-otp',
          component: 'CoreAuthContext',
        },
        extra: {
          email: email,
          shouldCreateUser: shouldCreateUser,
        },
      });
      throw error;
    }
  };

  const verifyOtp = async (email: string, token: string, type: 'signup' | 'recovery' | 'email_change' | 'email') => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.auth.verifyOtp({
        email,
        token,
        type
      });

      if (error) {
        // Log failed OTP verification
        await authAudit.logFailedLogin(email, `OTP verification failed: ${error.message}`);
        throw new Error(getAuthErrorMessage(error));
      }

      // Manually trigger session update since onAuthStateChange might not fire immediately
      if (data.session) {
        setSession(data.session);
        setSupabaseUser(data.session.user);
        
        sessionCacheRef.current = {
          session: data.session,
          timestamp: Date.now()
        };
        
        // Start session activity tracking
        sessionActivity.start(data.session.access_token.substring(0, 8));
        
        // Log successful OTP login
        await authAudit.logLogin(data.session.user.id, 'otp', { type });
      }
    } catch (error: any) {
      console.error('OTP verification error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const resetPasswordWithOtp = async (email: string) => {
    try {
      // Use signInWithOtp for password recovery
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: false // Don't create new users for password reset
        }
      });

      if (error) throw error;
    } catch (error) {
      console.error('Reset password with OTP error:', error);
      throw error;
    }
  };

  return (
    <CoreAuthContext.Provider value={{
      session,
      supabaseUser,
      isLoading,
      isInitialized,
      login,
      logout,
      loginWithGoogle,
      register,
      resetPassword,
      resendEmailConfirmation,
      refreshSession,
      signInWithOtp,
      verifyOtp,
      resetPasswordWithOtp
    }}>
      {children}
    </CoreAuthContext.Provider>
  );
}

export function useCoreAuth() {
  const context = useContext(CoreAuthContext);
  if (context === undefined) {
    throw new Error('useCoreAuth must be used within a CoreAuthProvider');
  }
  return context;
}